import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  getBranchLogoUrl,
  getBranchLogoUrlFor,
  getLogoUrl,
  getLogoUrlFor,
} from 'src/Common/common';

// Logo block for the WO form builder. Resolves, in order:
//
//   1. the branch's uploaded logo, for the branch of the work order this form is
//      on (public/<company>/branch/branch_<id>_logo.png)
//   2. the company's logo (public/<company>/company/logo.png)
//   3. nothing — the block hides itself
//
// Falling through on the image's own onError rather than checking first: these
// are static files, so a HEAD request to test for them would cost a round trip
// to say what the <img> tells us for free.
//
// The branch comes from window.__makaniFormBranchId, set by whichever surface is
// rendering the form — the same channel TechnicianDropdown uses for the work
// order id. In the builder, and on a form not attached to a work order, there is
// no branch, so the company logo is used.
//
// __makaniFormCompany matters just as much. The technician's form viewer is a
// PUBLIC route (/form-viewer/:formId) with no logged-in user, so the session
// based helpers resolve /files/undefined/... and 404. When the surface knows the
// tenant from the form payload it publishes it here, and the URL is built from
// that instead.
const CompanyLogo = ({
  width = 140,
  height = 'auto',
  alt = 'Company Logo',
  sx = {},
  ...rest
}) => {
  const [hidden, setHidden] = useState(false);
  // Which source is being attempted: 'branch' first, then 'company'.
  const [source, setSource] = useState('branch');

  const branchId = Number(window.__makaniFormBranchId) || null;
  const company = window.__makaniFormCompany || null;

  // Re-attempt from the top when the branch or tenant changes — the viewer modal
  // can move between forms on different work orders without remounting this.
  useEffect(() => {
    setSource(branchId ? 'branch' : 'company');
    setHidden(false);
  }, [branchId, company]);

  // Memoised so the src is stable across renders. The session-based helpers
  // append a cache-buster, so recomputing on every render would refetch the
  // image each time.
  const src = useMemo(() => {
    if (source === 'branch' && branchId) {
      return company
        ? getBranchLogoUrlFor(company, branchId)
        : getBranchLogoUrl(branchId);
    }
    return company ? getLogoUrlFor(company) : getLogoUrl();
  }, [source, branchId, company]);

  const handleError = () => {
    if (source === 'branch') {
      setSource('company');
      return;
    }
    setHidden(true);
  };

  return (
    <Box
      {...rest}
      sx={{
        width,
        height,
        minHeight: height === 'auto' ? 80 : undefined,
        display: 'grid',
        placeItems: 'center',
        alignSelf: 'center',
        my: 'auto',
        overflow: 'hidden',
      }}
    >
      {!hidden && (
        <Box
          component="img"
          src={src}
          alt={alt}
          onError={handleError}
          sx={{
            width: 'auto',
            height: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            objectPosition: 'center center',
            display: 'block',
            ...sx,
          }}
        />
      )}
    </Box>
  );
};

export default CompanyLogo;
